import { z } from 'zod';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  AuthenticatorBodySchema,
  AuthenticatorReturnSchema,
  CachedCredentialsResponseSchema,
  CachedCredentialResponseSchema,
  CachedCredentialsSummaryResponseSchema,
  RefreshCredentialBodySchema,
  RefreshCredentialResponseSchema,
} from './authenticator.schema';
import { AUTHENTICATOR_PATH_NAME } from './authenticator.constants';

export const AuthenticatorContract = terraContract
  .prefix(`/${AUTHENTICATOR_PATH_NAME}`)
  .router({
    validateAuthentication: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/validate-authentication',
        summary: 'Validate authentication',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: AuthenticatorBodySchema }))
      .output(AuthenticatorReturnSchema),
  });

export const CachedCredentialsContract = terraContract
  .prefix('/credentials')
  .router({
    getCachedCredentials: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/cached',
        summary:
          'Get cached credentials with auth data (headers, cookies, secrets) for an application',
        description:
          'Returns all cached credentials for an application, including their authenticated headers, cookies, and secrets. Used by the Replay tab to quickly switch between user contexts.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ applicationId: z.string() }) }))
      .output(CachedCredentialsResponseSchema),
    getCachedCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/cached/{credentialId}',
        summary: 'Get a single cached credential by ID',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
            credentialId: z.string(),
          }),
        }),
      )
      .output(CachedCredentialResponseSchema),
    getCachedCredentialsSummary: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/summary',
        summary: 'Get masked cached credential summaries for settings UI',
        description:
          'Returns gathered auth artifact names with masked values and expiry metadata without exposing secret values.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ applicationId: z.string() }) }))
      .output(CachedCredentialsSummaryResponseSchema),
    refreshCredential: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{applicationId}/refresh/{credentialId}',
        summary: 'Refresh a credential by triggering re-authentication',
        description:
          'Triggers the login flow for the specified credential and updates the cached auth data. Returns the updated credential with fresh headers, cookies, and secrets.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
            credentialId: z.string(),
          }),
          body: RefreshCredentialBodySchema,
        }),
      )
      .output(RefreshCredentialResponseSchema),
  });
